import { type FormEvent, useEffect, useMemo, useState } from "react";

import FinanceDateFilter from "../../components/finance/FinanceDateFilter";
import FinanceFilterField from "../../components/finance/FinanceFilterField";
import FinanceMultiSelect from "../../components/finance/FinanceMultiSelect";
import FinanceFilterPanel from "../../components/finance/FinanceFilterPanel";
import FinanceHelpLabel from "../../components/finance/FinanceHelpLabel";
import FinanceIncompleteBadge from "../../components/finance/FinanceIncompleteBadge";
import FinancePagination from "../../components/finance/FinancePagination";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import { fetchFinanceCustomers } from "../../finance/customer-api";
import type {
  FinanceCustomerFilters,
  FinanceCustomersResponse,
} from "../../finance/customer-types";
import {
  financeRangeFromSearch,
  financeRangeSearch,
  type FinanceDateRangeValue,
} from "../../finance/dates";
import { formatMoney, formatPercent } from "../../finance/formatting";

const initialFilters: FinanceCustomerFilters = {
  currencyIds: [],
  profitabilityStatuses: [],
  customerActivities: [],
  classIds: [],
  categoryIds: [],
  productIds: [],
  sort: "gross_profit",
};

function labels(items: Array<{ label: string }>) {
  return items.length > 0 ? items.map((item) => item.label).join(", ") : "—";
}

export default function FinanceCustomers() {
  const [range, setRange] = useState<FinanceDateRangeValue>(() =>
    financeRangeFromSearch(window.location.search),
  );
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] =
    useState<FinanceCustomerFilters>(initialFilters);
  const [report, setReport] = useState<FinanceCustomersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchFinanceCustomers(range, page, search, filters)
      .then((response) => active && setReport(response))
      .catch(
        () => active && setError("Customer profitability could not be loaded."),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [range.from, range.to, page, search, filters]);

  const updateFilter = <K extends keyof FinanceCustomerFilters>(
    key: K,
    value: FinanceCustomerFilters[K],
  ) => {
    setFilters((current) => {
      const next = { ...current, [key]: value };
      if (key === "classIds") {
        next.categoryIds = [];
        next.productIds = [];
      }
      if (key === "categoryIds") next.productIds = [];
      return next;
    });
    setPage(1);
  };
  const categories = useMemo(
    () =>
      report?.filters.categories.filter(
        (option) =>
          filters.classIds.length === 0 ||
          (option.class_id !== null &&
            filters.classIds.includes(option.class_id)),
      ) ?? [],
    [report, filters.classIds],
  );
  const products = useMemo(
    () =>
      report?.filters.products.filter(
        (option) =>
          (filters.classIds.length === 0 ||
            (option.class_id !== null &&
              filters.classIds.includes(option.class_id))) &&
          (filters.categoryIds.length === 0 ||
            (option.category_id !== null &&
              filters.categoryIds.includes(option.category_id))),
      ) ?? [],
    [report, filters.classIds, filters.categoryIds],
  );
  const moreCount = [
    filters.productIds,
    filters.sort !== "gross_profit" ? filters.sort : "",
  ].filter((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value),
  ).length;
  const hasFilters =
    search !== "" ||
    Object.entries(filters).some(
      ([key, value]) =>
        key !== "sort" && Array.isArray(value) && value.length > 0,
    ) ||
    filters.sort !== "gross_profit";

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-700">Finance</p>
            <h1 className="text-2xl font-semibold text-slate-900">
              Customer Profitability
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Compare customer sales, material costs, Gross Profit, margins, and
              credit activity.
            </p>
          </div>
          <FinanceDateFilter
            value={range}
            onChange={(value) => {
              setRange(value);
              setPage(1);
            }}
          />
        </div>

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <FinanceFilterPanel
            additionalFilterCount={moreCount}
            hasActiveFilters={hasFilters}
            onClear={() => {
              setFilters(initialFilters);
              setSearch("");
              setSearchDraft("");
              setPage(1);
            }}
            primary={
              <>
                <FinanceFilterField label="Currency">
                  <FinanceMultiSelect
                    value={filters.currencyIds}
                    placeholder="All currencies"
                    options={
                      report?.filters.currencies.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("currencyIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Profitability status">
                  <FinanceMultiSelect<
                    "profitable" | "unprofitable" | "incomplete"
                  >
                    value={filters.profitabilityStatuses}
                    placeholder="All statuses"
                    options={[
                      { label: "Profitable", value: "profitable" },
                      { label: "Unprofitable", value: "unprofitable" },
                      { label: "Missing costs", value: "incomplete" },
                    ]}
                    onChange={(value) =>
                      updateFilter("profitabilityStatuses", value)
                    }
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Customer activity">
                  <FinanceMultiSelect<"active" | "no_sales" | "has_credits">
                    value={filters.customerActivities}
                    placeholder="All activity"
                    options={[
                      { label: "Has sales", value: "active" },
                      { label: "No positive sales", value: "no_sales" },
                      { label: "Has credits", value: "has_credits" },
                    ]}
                    onChange={(value) =>
                      updateFilter("customerActivities", value)
                    }
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Class">
                  <FinanceMultiSelect
                    value={filters.classIds}
                    placeholder="All classes"
                    options={
                      report?.filters.classes.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("classIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Category">
                  <FinanceMultiSelect
                    value={filters.categoryIds}
                    placeholder="All categories"
                    options={categories.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    onChange={(value) => updateFilter("categoryIds", value)}
                  />
                </FinanceFilterField>
              </>
            }
            additional={
              <>
                <FinanceFilterField label="Product">
                  <FinanceMultiSelect
                    value={filters.productIds}
                    placeholder="All products"
                    options={products.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    onChange={(value) => updateFilter("productIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Sort">
                  <select
                    value={filters.sort}
                    onChange={(event) =>
                      updateFilter(
                        "sort",
                        event.target.value as FinanceCustomerFilters["sort"],
                      )
                    }
                  >
                    <option value="gross_profit">Gross Profit</option>
                    <option value="net_sales">Net Sales</option>
                    <option value="gross_margin">Margin</option>
                    <option value="credits">Credits</option>
                    <option value="shipped_order_count">Order Count</option>
                  </select>
                </FinanceFilterField>
              </>
            }
          />

          <div className="flex flex-col gap-3 border-b border-slate-200 p-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                Customer profitability
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Filters and date changes refresh automatically.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <form className="flex gap-2" onSubmit={submitSearch}>
                <input
                  aria-label="Search customer"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Customer or number"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                />
                <button
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                  type="submit"
                >
                  Search
                </button>
              </form>
            </div>
          </div>

          {error && (
            <p className="p-5 text-sm font-medium text-red-700">{error}</p>
          )}
          {!error && loading && (
            <div className="p-3">
              <TableLoadingSkeleton rows={7} columns={6} />
            </div>
          )}
          {!error && !loading && report?.data.length === 0 && (
            <p className="p-5 text-sm text-slate-500">
              No customers match these filters.
            </p>
          )}
          {!error && !loading && report && report.data.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    <tr>
                      {[
                        "Customer",
                        "Net Sales",
                        "COGS",
                        "Gross Profit",
                        "Margin",
                        "Average Order",
                        "Credit Rate",
                        "Products / Categories",
                        "Status",
                      ].map((label) => (
                        <th key={label} className="px-4 py-3">
                          <FinanceHelpLabel label={label} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.data.map((customer) => (
                      <tr
                        key={`${customer.customer_id}-${customer.currency_id}`}
                      >
                        <td className="px-4 py-4">
                          <a
                            className="font-semibold text-emerald-700 hover:underline"
                            href={`/finance/customers/${customer.customer_id}${financeRangeSearch(
                              range,
                              { currency: customer.currency_id },
                            )}`}
                          >
                            {customer.customer_name}
                          </a>
                          <p className="text-xs text-slate-500">
                            {customer.customer_number || "No customer number"} ·{" "}
                            {customer.customer_type_name || "No customer type"}{" "}
                            · {customer.currency}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          {formatMoney(customer.net_sales, customer.currency)}
                        </td>
                        <td className="px-4 py-4">
                          {formatMoney(customer.cogs, customer.currency)}
                        </td>
                        <td className="px-4 py-4 font-medium">
                          {formatMoney(
                            customer.gross_profit,
                            customer.currency,
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {formatPercent(customer.gross_margin)}
                        </td>
                        <td className="px-4 py-4">
                          {formatMoney(
                            customer.average_shipped_order_value,
                            customer.currency,
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {formatPercent(customer.return_credit_rate)}
                        </td>
                        <td className="max-w-xs px-4 py-4 text-xs text-slate-600">
                          <p>{labels(customer.top_products)}</p>
                          <p className="mt-1 text-slate-400">
                            {labels(customer.top_categories)}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          {customer.complete ? (
                            <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                              Complete
                            </span>
                          ) : (
                            <FinanceIncompleteBadge
                              count={
                                customer.missing_cost_shipments +
                                customer.missing_price_shipments
                              }
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <FinancePagination
                label="results"
                page={report.pagination.current_page}
                lastPage={report.pagination.last_page}
                total={report.pagination.total}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>
    </main>
  );
}
